import { EMAIL_PATTERN } from '@psyomics/ui-common';
import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../atoms/button/button';
import { Header } from '../../../atoms/header/header';
import { Input } from '../../../atoms/input/input';
import { Label } from '../../../atoms/label/label';
import { PhoneInput } from '../../../atoms/phone-input/phone-input';
import { Text } from '../../../atoms/text/text';
import { PASSWORD_MIN_LENGTH, PASSWORD_PATTERN } from '../password-validation';

import { Information } from './information';
import {
  FormFieldProps,
  PasswordFieldsProps,
  PasswordInputProps,
  RegistrationMethod,
  UsernameFieldProps,
  UsernameInputProps,
} from './types';

const UsernameInput = ({ control, registrationMethod, error, setError, clearErrors }: UsernameInputProps) => {
  const { t } = useTranslation();
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(true);
  const [isPhoneCountryValid, setIsPhoneCountryValid] = useState(true);

  const isEmailInput = registrationMethod === 'email';

  const usernamePlaceholder = t(isEmailInput ? 'auth.common.emailPlaceholder' : 'auth.common.mobilePlaceholder');
  const usernameRules = useUsernameFieldRules(isEmailInput, isPhoneNumberValid && isPhoneCountryValid);

  useEffect(() => {
    !isEmailInput && !isPhoneCountryValid
      ? setError('username', { message: t('auth.common.errors.phoneNumberInvalid') })
      : clearErrors('username');
  }, [clearErrors, setError, isPhoneCountryValid, registrationMethod, isEmailInput, t]);

  return (
    <Controller
      control={control}
      name="username"
      render={({ field: { name, onBlur, onChange, ref, value } }) =>
        isEmailInput ? (
          <Input
            name={name}
            onBlur={onBlur}
            onChange={onChange}
            placeholder={usernamePlaceholder}
            ref={ref}
            type="email"
            value={value}
            variant={error ? 'error' : 'default'}
          />
        ) : (
          <div className="w-full">
            <PhoneInput
              inputMode="tel"
              name={name}
              onBlur={onBlur}
              onChangeNumber={onChange}
              placeholder={usernamePlaceholder}
              setIsValidCountry={setIsPhoneCountryValid}
              setIsValidNumber={setIsPhoneNumberValid}
              value={value}
              variant={error ? 'error' : 'default'}
            />
          </div>
        )
      }
      rules={usernameRules}
    />
  );
};

const UsernameField = ({
  clearErrors,
  control,
  error,
  register,
  setValue,
  defaultMethod = 'email',
  allowToggle = true,
  setError,
}: UsernameFieldProps) => {
  const { t } = useTranslation();
  const [registrationMethod, setRegistrationMethod] = useState<RegistrationMethod>(defaultMethod);

  const usernameLabel = t(registrationMethod === 'email' ? 'auth.common.emailLabel' : 'auth.common.mobileLabel');
  const toggleLabel = t(
    registrationMethod === 'email'
      ? 'auth.common.switchToMobileUsernameLabel'
      : 'auth.common.switchToEmailUsernameLabel'
  );

  useEffect(() => {
    setValue('registrationMethod', registrationMethod);
  }, [registrationMethod, setValue]);

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor="username">{usernameLabel}</Label>
      <UsernameInput
        clearErrors={clearErrors}
        control={control}
        error={error}
        registrationMethod={registrationMethod}
        setError={setError}
      />
      <input type="hidden" {...register('registrationMethod')} />
      {allowToggle && (
        <Button
          appearance="secondary"
          onClick={(e) => {
            e.preventDefault();
            setRegistrationMethod(registrationMethod === 'email' ? 'phone' : 'email');
          }}
          type="button"
        >
          {toggleLabel}
        </Button>
      )}
    </div>
  );
};

const PasswordInput = ({ setShowPassword, repeat, variant, ...props }: PasswordInputProps) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor="password">{repeat ? t('auth.common.repeatPasswordLabel') : t('auth.common.passwordLabel')}</Label>
      <Input
        placeholder={t('auth.common.passwordPlaceholder')}
        suffix={
          <Text className="text-neutral-700 cursor-pointer" onClick={() => setShowPassword((s) => !s)} tag="p">
            {t('auth.common.showPassword')}
          </Text>
        }
        variant={variant}
        {...props}
      />
      {!repeat && <Text size="sm">{t('auth.common.errors.passwordFormatDescription')}</Text>}
    </div>
  );
};

const PasswordFields = ({
  setShowPassword,
  passwordInputType,
  errors,
  register,
  t,
  watchPassword,
}: PasswordFieldsProps) => (
  <>
    <PasswordInput
      setShowPassword={setShowPassword}
      type={passwordInputType}
      variant={errors.password ? 'error' : 'default'}
      {...register('password', {
        required: t('auth.common.errors.passwordRequired'),
        pattern: {
          value: PASSWORD_PATTERN,
          message: t('auth.common.errors.passwordFormatDescription'),
        },
        minLength: {
          value: PASSWORD_MIN_LENGTH,
          message: t('auth.common.errors.passwordTooShort'),
        },
      })}
    />
    <PasswordInput
      repeat
      setShowPassword={setShowPassword}
      type={passwordInputType}
      variant={errors.confirmPassword ? 'error' : 'default'}
      {...register('confirmPassword', {
        required: t('auth.common.errors.passwordConfirmationMissing'),
        validate: (value: string) => value === watchPassword || t('auth.common.errors.passwordsDoNotMatch'),
      })}
    />
  </>
);

const Heading = ({ passwordLess }: { readonly passwordLess?: boolean }) => {
  const { t } = useTranslation();
  const headerKey = passwordLess ? 'auth.register.passwordLessHeader' : 'auth.register.mainHeader';
  const headerLevel = passwordLess ? 'h2' : 'h1';
  return <Header level={headerLevel}>{t(headerKey)}</Header>;
};

const useUsernameFieldRules = (isEmailInput: boolean, isValid: boolean) => {
  const { t } = useTranslation();
  return isEmailInput
    ? {
        required: t('auth.common.errors.emailRequired'),
        pattern: {
          value: EMAIL_PATTERN,
          message: t('auth.common.errors.emailInvalid'),
        },
        validate: undefined,
      }
    : {
        required: t('auth.common.errors.phoneNumberRequired'),
        pattern: undefined,
        validate: (value: string) => {
          if (value?.trim().length && !isValid) return t('auth.common.errors.phoneNumberInvalid');
          return true;
        },
      };
};

const FormFields = ({
  control,
  clearErrors,
  errors,
  passwordInputType,
  register,
  setValue,
  setShowPassword,
  watchPassword,
  passwordLess,
  defaultMethod,
  allowToggle,
  setError,
}: FormFieldProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-6">
      <Heading passwordLess={passwordLess} />
      {!passwordLess && <Information />}
      <UsernameField
        allowToggle={allowToggle}
        clearErrors={clearErrors}
        control={control}
        defaultMethod={defaultMethod}
        error={errors.username}
        register={register}
        setError={setError}
        setValue={setValue}
      />
      {!passwordLess && (
        <PasswordFields
          errors={errors}
          passwordInputType={passwordInputType}
          register={register}
          setShowPassword={setShowPassword}
          t={t}
          watchPassword={watchPassword}
        />
      )}
    </div>
  );
};

export { FormFields };
